import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  viewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  block,
  Command2,
  handleRequestError,
  MenuItem,
  Scan,
  ScannerStore,
  showRequestErrors,
  SOFTLINE_FEATURE_SCANNER,
  WithActivatedRoute,
  WithCommands2,
  WithMenuItems,
  WithRefresh,
  WithRouter,
  WithScan,
  WithStaticBackNavigation,
  WithTitle,
} from '@softline/application';
import { PageComponent, PageHeaderComponent } from '@softapps/core';
import { I18nModule, MessageBarService, UiCoreModule } from '@softline/ui-core';
import { AuftragStore } from '../../store/auftrag.store';
import { Kopfau, KopfauWithMetadata } from '../../types/kopfau';
import { DashboardCardComponent } from '../../components/dashboard-card/dashboard-card.component';
import { LocalStorageDataAccess, Store } from '@softline/core';
import { AuftragService } from '../../services/auftragService';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatestWith, map } from 'rxjs/operators';

@Component({
  selector: 'soft-dashboard',
  imports: [
    CommonModule,
    I18nModule,
    UiCoreModule,
    DashboardCardComponent,
    PageComponent,
    PageHeaderComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent
  extends WithTitle(
    WithRefresh(
      WithCommands2(
        WithStaticBackNavigation(
          '/',
          WithMenuItems(
            WithScan(
              {
                labelType: ['code39', 'ean13', 'code128', 'ean128', 'itf', 'qrcode'],
              },
              WithRouter(WithActivatedRoute())
            )
          )
        )
      )
    )
  )
  implements OnInit, OnDestroy
{
  private readonly injector = inject(Injector);
  private readonly zone = inject(NgZone);
  private readonly store = inject(Store);
  private auftraegeElements = viewChildren(DashboardCardComponent);

  private messageBarService = inject(MessageBarService);

  readonly WwsAuftragStore = inject(AuftragStore);
  readonly localStorage = inject(LocalStorageDataAccess);
  readonly title = signal('#GOLDSCHMIEDE.DASHBOARD.TITLE');

  readonly auftragService = inject(AuftragService);

  pollingIntervall: any;

  readonly auftraege: Signal<KopfauWithMetadata[]> =
    this.WwsAuftragStore.auftragStoreAddon.nonFinishedAuftraege;

  readonly highlightedItem = signal<{ id: number | null }>({ id: null });
  readonly loading = this.WwsAuftragStore.collectionRemote.loadingState;
  readonly saving = signal(false);
  readonly finishedAuftraege = this.WwsAuftragStore.auftragStoreAddon.finishedAuftraege;

  readonly groupedAuftraege = computed(() => {
    const auftraege = this.auftraege();
    const groups: Record<number, KopfauWithMetadata[]> = {};
    const idKundenauftraege = [
      ...new Set(auftraege.map((o) => o.kopfau?.idkundenauftrag).filter((o) => !!o)),
    ];

    for (const id of idKundenauftraege) {
      if (!id) return;
      groups[id] = [
        ...(auftraege?.filter((o) => o?.kopfau?.idkundenauftrag === id) ?? 0),
      ];
    }

    return Object.values(groups) ?? [];
  });

  readonly allPaused = computed(() => {
    const auftraege = this.auftraege();
    let count = 0;

    auftraege
      .map(({ kopfau }) => kopfau?.status)
      .filter((status) => status === 'PAUSIERT')
      .forEach(() => count++);

    return count === auftraege?.length;
  });

  readonly loadingAuftraege$ = toObservable(
    this.WwsAuftragStore.collectionRemote.loadingState
  );
  readonly auftragCount$ = toObservable(this.WwsAuftragStore.collection.count);

  commands = computed<Command2[]>(() => {
    const commands: Command2[] = [
      {
        name: 'Aktualisieren',
        execute: async () => {
          await this.WwsAuftragStore.collectionRemote.loadMany();
        },
      },
      {
        name: 'DashboardComponent.PauseMany',
        canExecute: this.loadingAuftraege$.pipe(
          combineLatestWith(this.auftragCount$),
          map(([o, count]) => o !== 'loading' && count > 0)
        ),
        execute: async () => {
          try {
            if (this.allPaused()) {
              await this.auftragService.startMany(
                this.auftraege()
                  .map((o) => o.kopfau)
                  .filter((o) => o.status === 'PAUSIERT')
              );
            } else {
              await this.auftragService.pauseMany(
                this.auftraege()
                  .map((o) => o.kopfau)
                  .filter((o) => o.status === 'GESTARTET')
              );
            }
          } catch (e) {
            handleRequestError(e, this.injector, { show: true });
          }
        },
      },
      {
        name: 'start_scan',
        execute: async () => {
          await this.store.dispatch(SOFTLINE_FEATURE_SCANNER, ScannerStore.actions.scan, {
            labelType: ['ean13', 'ean8', 'code39', 'itf', 'code128', 'upc'],
          });
        },
      },
    ];
    return commands;
  });

  menuItems = computed<MenuItem[]>(() => [
    {
      name: 'DashboardComponent.PauseMany',
      type: 'command',
      class: 'soft-button gap-2 accent w-full',
      title: this.allPaused() ? 'Alle Starten' : 'Alle Pausieren',
      outlet: 'responsive',
      icon: this.allPaused()
        ? 'fa-regular fa-play self-center'
        : 'fa-regular fa-pause self-center',
    },
    {
      name: 'start_scan',
      type: 'command',
      outlet: 'action',
      title: 'Scanner starten',
      icon: 'fa-regular fa-barcode self-center',
    },
  ]);

  constructor() {
    super();
  }

  override async ngOnInit(): Promise<void> {
    super.ngOnInit();

    // Check if there is a `abteilung` set in localstorage
    // If not we show a warning, because we need to send idabteilung on scan
    await this.getDepartmentId();

    await this.WwsAuftragStore.collectionRemote.loadMany();

    this.pollingIntervall = setInterval(async () => {
      await this.WwsAuftragStore.collectionRemote.loadMany();
    }, 180000);
  }

  override async refresh(): Promise<void> {
    try {
      await this.WwsAuftragStore.collectionRemote.loadMany();
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  override async onScan(scan: Scan): Promise<void> {
    if (!scan?.data || !scan.labelType) return;

    const departmentId = await this.getDepartmentId();

    if (!departmentId) {
      return;
    }

    try {
      const result = await block(() => {
        return this.auftragService.scanAdd(
          scan?.labelType ?? 'code128',
          scan.data,
          departmentId
        );
      }, this.injector);

      if (result.goldschmiede && result.goldschmiede.length > 0) {
        this.WwsAuftragStore.collection.addOrUpdateMany(result.goldschmiede);
        this.markItem(result.goldschmiede[0].idkundenauftrag, 500);
      } else if (result.alreadyAssigned && result?.goldschmiede?.length > 0) {
        this.markItem(result.goldschmiede[0].idkundenauftrag);
      }
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.pollingIntervall) clearInterval(this.pollingIntervall);
  }

  async onStatusChanged(kopfau: Kopfau | undefined, event: string): Promise<void> {
    console.log('on status change: ', kopfau, event);

    if (!kopfau) return;

    try {
      await this.WwsAuftragStore.auftragStoreAddon.changeStatus(kopfau);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  async onDetailPressed(kopfau: Kopfau | undefined) {
    await this.router.navigate([kopfau?.id], { relativeTo: this.route });
  }

  private markItem(idKundenauftrag: number | string | undefined, delay = 250) {
    if (!idKundenauftrag) return;

    this.delayed(delay, () => {
      this.highlightedItem.set({ id: +idKundenauftrag });
      this.auftraegeElements()
        ?.find((o) => o.auftrag()?.kopfau?.idkundenauftrag === +idKundenauftrag)
        ?.scrollIntoView();
    });
  }

  private delayed(delayInMilliseconds: number, fn: () => void): void {
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.zone.run(() => {
          fn();
        });
      }, delayInMilliseconds);
    });
  }

  private async getDepartmentId(): Promise<string | null> {
    // Check if there is a `abteilung` set in localstorage
    // If not we show a warning, because we need to send idabteilung on scan
    try {
      const id = await this.localStorage.get<string>({ key: 'abteilung' });

      if (!id)
        this.messageBarService.warning(
          'Um Aufträge via Scan zuzuweisen, muss die Abteilung in den Einstellungen hinterlegt werden!',
          'Keine Abteilung!'
        );

      return id;
    } catch {
      this.messageBarService.warning(
        'Um Aufträge via Scan zuzuweisen, muss die Abteilung in den Einstellungen hinterlegt werden!',
        'Keine Abteilung!'
      );

      return null;
    }
  }
}
